/* eslint-disable react/prop-types */
import React, {createContext, FC, ReactNode, useEffect, useRef} from 'react';
import {useIsFocused} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {noop} from 'lodash';

import StorybookScreen from '/component/screen/Storybook';
import {withStatusBar} from '/provider/StatusBarProvider';

export const StorybookContext = createContext({});
interface StorybookProviderProps {
  children: ReactNode;
  statusBarSetDarkText: () => void;
}

interface NavigationProps {
  navigation: {
    navigate: (screen: string) => void;
    canGoBack?: () => boolean;
    goBack?: () => void;
  };
}
export class StorybookProvider extends React.PureComponent<StorybookProviderProps> {
  private screenComponents!: {
    content: FC<NavigationProps>;
    storybook: FC<NavigationProps>;
  };

  private storybookStack = createStackNavigator();

  constructor(props: StorybookProviderProps) {
    super(props);
    this.initializeComponents();
  }

  initializeComponents() {
    this.screenComponents = {
      content: this.getContentScreenComponent(),
      storybook: this.getStorybookScreenComponent(),
    };
  }

  getContentScreenComponent(): FC<NavigationProps> {
    const {children} = this.props;

    return ({navigation: {navigate}}) => {
      const isFocused = useIsFocused();

      useEffect(() => {
        if (isFocused) {
          // Optional side effect on focus
        }
      }, [isFocused]);

      const handleStorybookOpenRef = useRef(() => navigate('storybook'));

      return (
        <StorybookContext.Provider
          value={{storybookOpen: handleStorybookOpenRef.current}}>
          {children}
        </StorybookContext.Provider>
      );
    };
  }

  getStorybookScreenComponent(): FC<NavigationProps> {
    const {statusBarSetDarkText = noop} = this.props;

    return ({
      navigation: {canGoBack = () => false, goBack = noop, navigate},
    }) => {
      const isFocused = useIsFocused();

      useEffect(() => {
        if (isFocused) {
          statusBarSetDarkText();
        }
      }, [isFocused]);

      const handleStorybookCloseRef = useRef(() => {
        if (canGoBack()) {
          goBack();
        } else {
          navigate('content-with-storybook');
        }
      });

      return (
        <StorybookScreen storybookClose={handleStorybookCloseRef.current} />
      );
    };
  }

  override render() {
    const StorybookStack = this.storybookStack;

    return (
      <StorybookStack.Navigator
        initialRouteName="content-with-storybook"
        screenOptions={{headerShown: false}}>
        <StorybookStack.Screen
          name="content-with-storybook"
          component={this.screenComponents.content}
        />
        <StorybookStack.Screen
          name="storybook"
          component={this.screenComponents.storybook}
        />
      </StorybookStack.Navigator>
    );
  }
}

export default withStatusBar(StorybookProvider);
